/**
 * Inline renderer for a KYC document ref. Fetches the bytes from
 * GET /api/documents/content (base64 + content type), converts to a data URL,
 * and renders an <img> for image content types or a download link for PDFs.
 *
 * Bytes come over the same authenticated channel as the rest of the admin —
 * no presigned-URL hop, so the API stays the single trust boundary.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DocumentContent } from "@dfp/api-client";
import { api } from "../lib/api.js";

/** Refs that look like real object-storage keys (countryId/kind/uuid.ext). */
function looksLikeStorageRef(ref: string): boolean {
  return ref.includes("/") && !ref.startsWith("pending-");
}

export function DocumentImage({
  refKey,
  label,
  className,
}: {
  readonly refKey: string;
  readonly label: string;
  readonly className?: string;
}): JSX.Element {
  const isReal = looksLikeStorageRef(refKey);
  const [previewOpen, setPreviewOpen] = useState(false);
  const query = useQuery({
    queryKey: ["documents", "content", refKey],
    queryFn: () =>
      api.get<DocumentContent>(`/api/documents/content?ref=${encodeURIComponent(refKey)}`),
    enabled: isReal,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!isReal) {
    return (
      <div className={frame(className)}>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-xs italic text-slate-400">Placeholder (not yet captured)</p>
      </div>
    );
  }
  if (query.isLoading) {
    return (
      <div className={frame(className)}>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-xs text-slate-400">Loading…</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className={frame(className)}>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-xs text-red-700">Failed to load.</p>
      </div>
    );
  }
  const dataUrl = `data:${query.data.contentType};base64,${query.data.dataBase64}`;
  const isImage = query.data.contentType.startsWith("image/");
  return (
    <div className={frame(className)}>
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
        <span className="text-[10px] text-slate-400">{query.data.contentType}</span>
      </div>
      {isImage ? (
        <>
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            className="mt-1 block w-full text-left focus:outline-none focus:ring-2 focus:ring-simuflex-green rounded-md overflow-hidden group relative"
          >
            <img
              src={dataUrl}
              alt={label}
              className="max-h-48 w-full rounded-md object-contain bg-slate-50 ring-1 ring-slate-200 group-hover:opacity-90 transition-opacity"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <span className="text-xs text-white font-bold bg-black/60 px-2 py-1 rounded-md shadow-sm backdrop-blur-sm">
                Preview 🔍
              </span>
            </div>
          </button>

          {previewOpen && (
            <div
              className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fade-in cursor-zoom-out"
              onClick={() => setPreviewOpen(false)}
            >
              <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewOpen(false);
                  }}
                  className="absolute top-[-44px] right-0 p-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-white"
                  title="Close Preview"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <img
                  src={dataUrl}
                  alt={label}
                  className="max-w-full max-h-[80vh] rounded-xl shadow-2xl border border-white/10 object-contain cursor-default"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="mt-3 text-center">
                  <p className="text-sm font-bold text-white tracking-wide uppercase">{label}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{query.data.contentType}</p>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <a
          href={dataUrl}
          download
          className="mt-1 inline-block text-xs text-brand-600 hover:underline"
        >
          Download ↗
        </a>
      )}
    </div>
  );
}

function frame(className?: string): string {
  return `rounded-md border border-slate-200 bg-white p-3 space-y-1 ${className ?? ""}`;
}
